import { useEffect, useState } from 'react';
import { faTag } from '@fortawesome/pro-regular-svg-icons';
import { Link, useParams } from 'react-router-dom';
import { NotificationDispatch } from '@tryhackme/thm-ui-components';
import deepEqual from 'fast-deep-equal';
import { ROOM_SCOPE } from 'src/common/constants/admin/tagging';
import { useAppSelector } from 'src/app/hooks';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { store } from 'src/app/store';
import { setIsDirty, setTagsList } from 'src/app/reducers/manage-room.reducer';
import { TagSelector } from 'src/features/manage-room/components/categories-tab/components/tag-selector/tag-selector';
import { RoomWeightedTag, TagCategory, useGetTagCategoriesQuery } from 'src/features/admin/tagging';
import { FormRow } from 'src/features/manage-room/components/form-row';
import { useFeatureIsOn } from 'src/common/growthbook';
import { SettingCard } from '../setting-card';
import { invalidateCachedCategories, useGetRoomTagsQuery, useUpdateCategoriesMutation, useUpdateRoomTagIdsMutation } from '../../manage-room.slice';
import { UpdateRoomConfirmModal } from '../update-room-confirm-modal';
import { DiscardChangesModal } from '../discard-changes-modal';
import { UnSavedWarnModal } from '../unsaved-warn-modal';
import { StyledContainer, StyledFindText, StyledInfoBox, StyledLine, StyledTagSelectors, StyledUpdateRoomButtonContainer } from './categories-tab.styles';
import { BrowseCategories, RoomCategories } from './components';
export const CategoriesTab = () => {
  const {
    roomCode
  } = useParams();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const {
    data: tagCategories
  } = useGetTagCategoriesQuery(null);
  const {
    data: roomTags = []
  } = useGetRoomTagsQuery({
    roomCode
  });
  const [updateTags] = useUpdateRoomTagIdsMutation();
  const [updateCategories, {
    isLoading
  }] = useUpdateCategoriesMutation();
  const {
    roomCategories,
    categoryDefaultValues,
    modifiedTags
  } = useAppSelector(state => state.manageRoom);
  const isWeightedTagsEnabled = useFeatureIsOn('weighted-room-tags');
  const isDirty = !deepEqual({
    values: roomCategories
  }, {
    values: categoryDefaultValues
  }) || !deepEqual({
    values: modifiedTags
  }, {
    values: roomTags
  });
  useEffect(() => {
    if (roomTags.length) {
      store.dispatch(setTagsList(roomTags));
    }
  }, [roomTags]);
  useEffect(() => {
    store.dispatch(setIsDirty(isDirty));
  }, [isDirty]);
  const handleUpdate = async () => {
    try {
      await Promise.all([handleUpdateTags(), handleUpdateCategories()]);
      NotificationDispatch('success', 'Room updated successfully.');
    } catch (error) {
      if (isQueryErrorWithMessage(error) && error.status === 400) {
        NotificationDispatch('error', error.data.message);
      }
      NotificationDispatch('error', 'Uh-oh. There was an error while updating your room. Please try again.');
    } finally {
      setOpenModal(false);
    }
  };
  const handleUpdateTags = async () => {
    if (!roomCode || modifiedTags.length <= 0) return;
    let payload: string[] | RoomWeightedTag[] = modifiedTags.map(tag => tag._id);
    if (isWeightedTagsEnabled) payload = modifiedTags.map(tag => ({
      id: tag._id,
      weight: tag.weight
    }));
    await updateTags({
      roomCode,
      payload
    });
  };
  const handleUpdateCategories = async () => {
    const {
      categories,
      OSTags: roomOSTags,
      PovTags
    } = roomCategories;
    const categoriesPayload = [...Object.values(categories), ...Object.values(roomOSTags), ...Object.values(PovTags)].map(category => ({
      ...category,
      id: category._id
    }));
    await updateCategories({
      roomCode: (roomCode as string),
      payload: categoriesPayload
    }).unwrap();
  };
  const handleDiscardChanges = () => {
    invalidateCachedCategories((roomCode as string));
  };
  return <section aria-label="Category Tab" data-sentry-component="CategoriesTab" data-sentry-source-file="categories-tab.tsx">
      <SettingCard title="Categories" icon={faTag} data-sentry-element="SettingCard" data-sentry-source-file="categories-tab.tsx">
        {tagCategories && <FormRow heading="Tags" description="Improve the discoverability of your room by adding tags">
            <StyledTagSelectors>
              {tagCategories.filter(tagCategory => tagCategory.scopes.includes(ROOM_SCOPE.value)).map((tagCategory: TagCategory) => <TagSelector key={tagCategory._id} category={tagCategory} existingRoomTags={roomTags || []} />)}
            </StyledTagSelectors>
          </FormRow>}
        <StyledContainer data-sentry-element="StyledContainer" data-sentry-source-file="categories-tab.tsx">
          <RoomCategories data-sentry-element="RoomCategories" data-sentry-source-file="categories-tab.tsx" />
          <BrowseCategories data-sentry-element="BrowseCategories" data-sentry-source-file="categories-tab.tsx" />
        </StyledContainer>

        <StyledInfoBox data-sentry-element="StyledInfoBox" data-sentry-source-file="categories-tab.tsx">
          <StyledFindText data-sentry-element="StyledFindText" data-sentry-source-file="categories-tab.tsx">
            Find all categories at{' '}
            <Link target="_blank" to="https://attack.mitre.org" data-sentry-element="Link" data-sentry-source-file="categories-tab.tsx">
              attack.mitre.org.
            </Link>
          </StyledFindText>
        </StyledInfoBox>

        <StyledLine data-sentry-element="StyledLine" data-sentry-source-file="categories-tab.tsx" />
      </SettingCard>

      <StyledUpdateRoomButtonContainer data-sentry-element="StyledUpdateRoomButtonContainer" data-sentry-source-file="categories-tab.tsx">
        {isDirty && <DiscardChangesModal handleActionButton={handleDiscardChanges} />}
        <UpdateRoomConfirmModal open={openModal} openChange={setOpenModal} isUpdateButtonDisabled={!isDirty} isRoomCodeChanged={false} isUpdatingRoom={isLoading}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      handleUpdateRoom={handleUpdate} data-sentry-element="UpdateRoomConfirmModal" data-sentry-source-file="categories-tab.tsx" />
      </StyledUpdateRoomButtonContainer>
      <UnSavedWarnModal isSavingChanges={isLoading} handleDiscardChanges={handleDiscardChanges}
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleActionButton={handleUpdateCategories} data-sentry-element="UnSavedWarnModal" data-sentry-source-file="categories-tab.tsx" />
    </section>;
};